/** @shell standard @header bar */
import React, { useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRoute } from '@react-navigation/native';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { cmsPageHtmlToDisplayText } from '../shared/utils/htmlToPlainText';
import { pickCmsPageImageUri } from '../shared/utils/resolveCmsImageUri';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { useTranslation } from '../shared/i18n';
import { useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { androidAppHeaderContentTop } from '../shared/theme/sharedStyles';
import {
  StackScreenShell,
  studioCardShell,
  DASH_STUDIO_RADIUS,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
} from '../shared/theme/studio/stackScreenShell';

function createNewsArticleStyles(c: ThemeColors) {
  return StyleSheet.create({
    scroll: {
      flex: 1,
    },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      gap: STACK_SECTION_GAP,
    },
    heroImage: {
      width: '100%',
      aspectRatio: 16 / 9,
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: c.borderLight,
    },
    bodyCard: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      padding: appTheme.spacing.lg,
    },
    bodyText: {
      fontSize: 15,
      lineHeight: 24,
      color: c.textSecondary,
    },
    fallbackText: {
      fontSize: 15,
      lineHeight: 22,
      color: c.textSecondary,
    },
  });
}

export const NewsArticleScreen: React.FC = () => {
  const on_back_exit = useExitOrGoBack();
  const route = useRoute();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const content_top = androidAppHeaderContentTop(insets.top);
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const article = (route.params as { article?: Record<string, unknown> })?.article;

  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createNewsArticleStyles(colors), [colors]);

  useEffect(() => {
    if (!article) {
      on_back_exit();
    }
  }, [article, on_back_exit]);

  if (!article) {
    return null;
  }

  const content =
    typeof article.displayContent === 'string'
      ? article.displayContent
      : String(article.content ?? article.a_content ?? '');
  const title = String(article.title ?? article.meta_title ?? article.name ?? t('news.articleFallback'));
  const body_text = cmsPageHtmlToDisplayText(content);
  const image_uri =
    (article as { resolvedImageUri?: string | null }).resolvedImageUri ?? pickCmsPageImageUri(article);

  return (
    <StackScreenShell colors={colors}>
      <ScreenHeader title={title} onBack={on_back_exit} scrollY={scrollY} />
      <Animated.ScrollView
        style={styles.scroll}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: content_top,
            paddingBottom: insets.bottom + STACK_SECTION_GAP * 2,
          },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        {image_uri ? (
          <Image source={{ uri: image_uri }} style={styles.heroImage} resizeMode="cover" accessibilityIgnoresInvertColors />
        ) : null}
        <View style={styles.bodyCard}>
          {body_text ? (
            <Text style={styles.bodyText} selectable>
              {body_text}
            </Text>
          ) : (
            <Text style={styles.fallbackText}>{t('newsArticle.noContent')}</Text>
          )}
        </View>
      </Animated.ScrollView>
    </StackScreenShell>
  );
};

export default NewsArticleScreen;
